// Thin fetch wrapper. Every request carries the session cookie via
// `credentials: "include"`. The SWR-backed hooks land in Unit 17 — this file
// is the single seam they share.

export type ApiInit = Omit<RequestInit, "credentials">;

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch(path: string, init: ApiInit = {}): Promise<Response> {
  // Don't set Content-Type for FormData — the browser must set it so the
  // multipart boundary is included automatically.
  const baseHeaders: Record<string, string> =
    init.body instanceof FormData ? {} : { "Content-Type": "application/json" };
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: { ...baseHeaders, ...(init.headers as Record<string, string> | undefined) },
  });
  // Notify AuthGuard of unexpected session expiry so it can redirect to /login.
  // Auth endpoints are excluded — their 401s are handled by the caller.
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    window.dispatchEvent(new Event("ophelia:unauthorized"));
  }
  return res;
}

export async function apiJson<T>(path: string, init: ApiInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, body, `${init.method ?? "GET"} ${path} → ${res.status}`);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
